import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, IconButton, Tab, Tabs, CircularProgress } from "@mui/material";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import { useGetShowroomByIdQuery } from "../../service/showroom/api";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel = ({ children, value, index }: TabPanelProps) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && children}
  </div>
);

const a11yProps = (index: number) => ({
  id: `tab-${index}`,
  "aria-controls": `tabpanel-${index}`,
});

const ManageDealersDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: showroomResponse, isLoading } = useGetShowroomByIdQuery(id);
  const showroom = showroomResponse?.data;
  const [activeTab, setActiveTab] = useState(0);

  if (isLoading) {
    return <Box display="flex" justifyContent="center" padding="50px"><CircularProgress /></Box>;
  }

  if (!showroom) {
    return <Box display="flex" justifyContent="center" padding="50px"><h2>No dealer details found.</h2></Box>;
  }

  const showroomInfo = [
    { label: "Showroom Name", value: showroom?.name || "N/A" },
    // { label: "Location Coordinates (Lat, Long)", value: showroom?.location?.coordinates ? `${showroom.location.coordinates[1]}, ${showroom.location.coordinates[0]}` : "N/A" },
    { label: "Registered Address", value: showroom?.address || "N/A" },
    { label: "Opening - Closing Time", value: `${showroom?.openingTime || "N/A"} - ${showroom?.closingTime || "N/A"}` },
    { label: "Email", value: showroom?.email || "N/A" },
    { label: "Phone", value: showroom?.phone || "N/A" },
    { label: "WhatsApp", value: showroom?.whatsappNumber || "N/A" },
    { label: "Description (English)", value: showroom?.description?.en || "N/A" },
    { label: "Description (Arabic)", value: showroom?.description?.ar || "N/A" },
  ];

  return (
    <>
      {/* Page Title */}
      <CommonBreadcrumb
        title="Showroom Detail"
        breadcrumbItems={[
          { label: "Dashboard", onClick: () => navigate("/dashboard") },
          { label: "Showrooms Management", onClick: () => navigate("/manage-users") },
          { label: "Showroom Detail" },
        ]}
      />

      {/* Tabs */}
      <div className="table_header">
        <Box className="custom_tabs1">
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            className="custom_tabs_links"
          >
            <Tab label="Profile Information" {...a11yProps(0)} />
            <Tab label="Documents" {...a11yProps(1)} />
          </Tabs>
        </Box>
      </div>

      {/* Profile Info */}
      <CustomTabPanel value={activeTab} index={0}>
        <div className="cards view_page">
          <div className="table_header mb_20">
            <h2>Showroom Information</h2>
            <span className={`status_btn sm ${showroom?.isActive ? "c_success" : "c_danger"}`}>
              {showroom?.isActive ? "Active" : "Inactive"}
            </span>
          </div>

          <div className="gap_p">
            <div className="w_100">
              <div className="gap_p">
                {/* Logo */}
                <div className="w_25">
                  <h6>Dealer Logo</h6>
                  <div className="vew_img no_radius v2">
                    <figure>
                      <img
                        src={showroom?.logo || "/static/images/placeholder.png"}
                        className="view_img"
                        alt="Logo"
                      />
                    </figure>
                  </div>
                </div>
              </div>
            </div>

            <div className="w_100">
              <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '20px 0' }} />
              <h6>Showroom Images</h6>
              <div className="gap_p">
                {showroom?.images?.length > 0 ? showroom.images.map((img: any, i: number) => (
                  <div className="w_25" key={i}>
                    <div className="vew_img no_radius v2">
                      <figure>
                        <img
                          src={img.url || "/static/images/placeholder.png"}
                          className="view_img"
                          alt="Showroom"
                        />
                      </figure>
                    </div>
                  </div>
                )) : (
                  <p>No images available</p>
                )}
              </div>
            </div>

            <div className="w_100">
              <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '20px 0' }} />
            </div>

            {showroomInfo.map((item, index) => (
              <div className="w_33" key={index}>
                <h6>{item.label}</h6>
                <p>{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </CustomTabPanel>
      {/* Documents */}
      <CustomTabPanel value={activeTab} index={1}>
        <div className="cards view_page">
          <div className="table_header mb_20">
            <h2>Documents</h2>
          </div>

          <div className="gap_p">
            {showroom?.documents?.length > 0 ? (
              showroom.documents.map((doc: any, i: number) => (
                <div className="w_100" key={i}>
                  <h6>{doc.documentType || "Document"} - <i>{doc.businessName}</i></h6>
                  <div className="doc_preview" style={{ maxWidth: '400px' }}>
                    <AttachFileIcon /> 
                    <p style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {doc.documentUrl?.split('/').pop() || doc.documentType}
                    </p> 
                    <IconButton 
                      className='btn_rounded primary' 
                      component="a" 
                      href={doc.documentUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <FileDownloadOutlinedIcon />
                    </IconButton>
                  </div>
                </div>
              ))
            ) : (
              <Box padding="20px">No documents found for this dealer.</Box>
            )}
          </div>
        </div>
      </CustomTabPanel>
    </>
  );
};

export default ManageDealersDetail;
